import { Link } from "react-router-dom";
import {
  Activity, Bell, Ban, BookOpenText, Building, ClipboardCheck, Copyright, ExternalLink, FileCheck2,
  Layers, LifeBuoy, ListChecks, Mail, RefreshCw, Settings2, ShieldAlert, UserCheck,
} from "lucide-react";
import LegalLayout from "../components/legal/LegalLayout";
import ClauseList from "../components/legal/ClauseList";
import { LegalCallout, LegalContact, PlatformScope } from "../components/legal/LegalBlocks";

const SCOPE_GROUPS = [
  {
    title: "Campus Services",
    icon: Building,
    items: ["Guest Room Booking", "Venue Booking", "Library Night Pass", "Society Night Permission", "Lost & Found"],
  },
  {
    title: "Campus Information",
    icon: BookOpenText,
    items: ["Student Notices", "Event Calendar", "Student Calendar", "Institute Calendar", "Student Societies"],
  },
  {
    title: "Student Support",
    icon: LifeBuoy,
    items: ["Student Grievance Portal", "Campus Feedback", "TIET Health Hub", "Public Forms & Downloads"],
  },
  {
    title: "Authorized Administration",
    icon: Settings2,
    items: ["Society Route Manager", "Manage Public Forms", "Public UI Management", "Echo Knowledge", "System Analytics"],
  },
];

const SECTIONS = [
  {
    id: "acceptance",
    title: "Introduction & Acceptance",
    icon: FileCheck2,
    body: (
      <>
        <p className="lc-lead">
          By accessing or using Thapar Campus Connect, you agree to these Terms and Conditions and to the
          applicable rules of Thapar Institute of Engineering &amp; Technology (TIET) and the Office of Student Affairs.
        </p>
        <p>
          These terms exist so that everyone can use the platform fairly and safely. If you do not agree with
          them, please do not use the platform. If anything is unclear, you are welcome to reach out using the
          contact details at the end of this page.
        </p>
      </>
    ),
  },
  {
    id: "about",
    title: "About Campus Connect",
    icon: Layers,
    body: (
      <>
        <p>
          Campus Connect is a unified digital platform for campus services, information, requests, approvals,
          communication, forms and authorized administrative tools. It brings several student and institutional
          services together in one place.
        </p>
        <PlatformScope groups={SCOPE_GROUPS} />
        <LegalCallout>
          This overview is descriptive only. Not every service is available to every user, and availability
          depends on your role, institutional authorization, the status of the service and applicable campus rules.
        </LegalCallout>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "Eligibility & Authorized Access",
    icon: UserCheck,
    body: (
      <>
        <p>Campus Connect services may be available to:</p>
        <ClauseList items={[
          "Students",
          "Faculty",
          "Staff",
          "Authorized administrators",
          "Other approved users, where applicable",
        ]} />
        <p style={{ marginTop: 14 }}>
          Access depends on your role and on the authorization granted to you by the institute. Some services
          and tools are visible only to specific users.
        </p>
      </>
    ),
  },
  {
    id: "responsibilities",
    title: "User Responsibilities",
    icon: ListChecks,
    body: (
      <>
        <p>As a user of Campus Connect, you are expected to:</p>
        <ClauseList items={[
          "Provide accurate and current information",
          "Use only your own authorized credentials",
          "Keep your account access secure",
          "Submit genuine forms, documents and information",
          "Use services only for legitimate institutional purposes",
          "Comply with applicable institute rules",
          "Respect other users and institutional systems",
        ]} />
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    icon: Ban,
    body: (
      <>
        <p>Please do not use Campus Connect for any of the following:</p>
        <ClauseList variant="avoid" items={[
          "Unauthorized access to accounts, data or administrative tools",
          "Sharing credentials or allowing others to use your account",
          "Impersonating another person or office",
          "Submitting false or misleading information",
          "Malicious interference with the platform or its users",
          "Exploiting or circumventing access controls",
          "Abusive, harassing or unlawful use",
          "Automated scraping or data extraction without authorization",
          "Misusing institutional data",
          "Attempting to disrupt the availability of the service",
        ]} />
      </>
    ),
  },
  {
    id: "requests",
    title: "Requests, Forms & Approvals",
    icon: ClipboardCheck,
    body: (
      <>
        <p>
          Some Campus Connect services involve requests, submissions or approvals. Submitting a request does not
          automatically guarantee that it will be approved.
        </p>
        <p>
          Decisions remain with authorized institutional personnel and are subject to applicable institute rules.
          Any instructions shown inside a specific service apply when you use that service.
        </p>
      </>
    ),
  },
  {
    id: "communications",
    title: "Communications & Notifications",
    icon: Bell,
    body: (
      <>
        <p>Campus Connect may send or display:</p>
        <ClauseList items={[
          "Email notifications",
          "Status updates",
          "Notices",
          "Reminders",
          "Other service-related messages",
        ]} />
        <p style={{ marginTop: 14 }}>
          You are responsible for reviewing official communications and keeping your contact details up to date.
        </p>
      </>
    ),
  },
  {
    id: "availability",
    title: "Service Availability",
    icon: Activity,
    body: (
      <>
        <p>
          Campus Connect is continuously evolving. Services and modules may change, be added or removed, or be
          temporarily unavailable, and maintenance may take place from time to time. Availability can also depend
          on your role and on the status of a given service.
        </p>
        <p>We work to keep the platform dependable, but we do not guarantee uninterrupted availability.</p>
      </>
    ),
  },
  {
    id: "external",
    title: "External Links & Services",
    icon: ExternalLink,
    body: (
      <p>
        Campus Connect may link to other institutional or third-party services. These external destinations are
        outside Campus Connect and may have their own terms and privacy practices. Please review them before you
        use them.
      </p>
    ),
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    icon: Copyright,
    body: (
      <>
        <p>
          Campus Connect branding, original design, custom software and institutional content remain subject to
          the rights of TIET and to applicable third-party licenses.
        </p>
        <p>
          Third-party open-source libraries remain the property of their respective owners and are governed by
          their own licenses. See the <Link to="/license">Software License</Link> page
          for more information.
        </p>
      </>
    ),
  },
  {
    id: "suspension",
    title: "Suspension or Restriction of Access",
    icon: ShieldAlert,
    body: (
      <>
        <p>Access to Campus Connect, or to specific services, may be restricted where necessary for:</p>
        <ClauseList items={[
          "Security",
          "Misuse of the platform",
          "Violations of applicable policies",
          "Institutional requirements",
        ]} />
        <p style={{ marginTop: 14 }}>
          Any restriction is applied in proportion to the situation and in line with institutional processes.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    icon: RefreshCw,
    body: (
      <p>
        These terms may be updated from time to time as Campus Connect grows. The date of the latest update is
        shown at the top of this page. Your continued use of the platform after an update is subject to the
        updated terms.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    icon: Mail,
    body: (
      <>
        <p>For questions about these terms, please contact:</p>
        <LegalContact />
      </>
    ),
  },
];

export default function TermsPage() {
  return (
    <LegalLayout
      current="terms"
      title="Terms & Conditions"
      subtitle="The terms that apply when you access or use the Thapar Campus Connect digital platform."
      sections={SECTIONS}
    />
  );
}
